import { ReactElement, useEffect, useRef, useState } from "react";
import { Stack } from "@mui/material";
import StackedBarGroups from "src/components/monitoring/senec-stacked-bar-chart/StackedBarGroups";
import { AutarkyLine } from "src/components/monitoring/senec-stacked-bar-chart/autarky/AutarkyLine";
import { LineChartItem } from "src/components/monitoring/senec-line-chart/lineChartTypes";
import { DataSeriesUnit, AxisConfig } from "src/components/monitoring/commonTypes";
import { ChartGrid } from "src/components/monitoring/axisLegends/ChartGrid";
import { YAxisLegend } from "src/components/monitoring/axisLegends/YAxisLegend";
import { getLabelsForYAxis } from "src/components/monitoring/axisUtils";
import { HoverItem } from "src/components/monitoring/senec-stacked-bar-chart/HoverItem";
import { useStackedBarWidth } from "src/components/monitoring/senec-stacked-bar-chart/useStackedBarWidth";

/**
 * Represents a single bar item in a stacked bar chart, consisting of three stacked bars. Important: all values per item should have the same unit.
 */
export type StackedBarItem = {
    /** The index for the group to which this item belongs. Currently restricted to 0, 1, or 2. */
    groupIndex: 0 | 1 | 2;

    /** Optional data for the lower segment of the stacked bar. */
    lower?: Bar;

    /** Optional data for the middle segment of the stacked bar. */
    middle?: Bar;

    /** Optional data for the upper segment of the stacked bar. */
    upper?: Bar;

    /** Flag to indicate if the stack consists of percentage values.. */
    isPercentageStack: boolean;
};

/**
 * Represents a single segment within a stacked bar.
 */
export type Bar = {
    /** The numerical value of the bar segment. */
    value: number;

    /** The color of the bar segment. Expressed as a CSS color string. */
    color: string;

    /** The segment label*/
    label: string;
};

/**
 * Represents the entire dataset for the stacked bar chart.
 */
export type StackedBarData = {
    /** Optional array of items to render as a stacked bar chart. */
    stackedBarItems?: StackedBarItem[];
    /** Label for all bar items, displayed as x-axis. */
    label?: string;
};

export type StackedBarChartProps = {
    /**
     * Flag to indicate if the chart is currently loading data. Needed for width calculations.
     * @deprecated has no visual effect
     */
    isLoading?: boolean;

    /** Array of datasets for rendering multiple stacked bar charts. */
    stackedBarData: StackedBarData[];

    /** @deprecated Whether to display the left Y-axis.  Use leftAxisConfig for left axis config */
    showLeftAxis?: boolean;

    /** @deprecated Whether to display the right Y-axis. Use rightAxisConfig for right axis config */
    showRightAxis?: boolean;

    /** Left axis config */
    leftAxisConfig?: AxisConfig;

    /** Right axis config */
    rightAxisConfig?: AxisConfig;

    /** Locale setting for the chart, used for formatting numbers. */
    locale: Intl.Locale;

    /** Amount of horizontal lines in the Chart. @default is 6 */
    chartLines?: number;

    /** Optional line chart data to overlay on top of the stacked bar chart. Can be used for autarky. */
    autarkyData?: LineChartItem;

    /** Optional callback function to get index when a timeframe is selected */
    getSelectedTimeFrameIndex?(index: number): void;

    /** Optional property defining if tooltip should be shown */
    showTooltip?: boolean;
};

/**
 *  Stacked bar chart that can render a combination of bar groups and an overlaid line graph, see {@link StackedBarChartProps.autarkyData}.
 *
 *  @param autarkyData rendered as linear line graph.
 *  @param locale for localizing numbers.
 *  @param showLeftAxis whether the left axis is shown.
 *  @param showRightAxis whether the right axis is shown.
 *  @param leftAxisConfig config for left axis.
 *  @param rightAxisConfig config for right axis.
 *  @param stackedBarData data for the stacked bar groups.
 *  @param getSelectedTimeFrameIndex called when the bar group with the given index is selected.
 *  @param chartLines amount of horizontal lines in the chart.
 * @param showTooltip
 */
export const SenecStackedBarChart = ({
    isLoading,
    stackedBarData,
    showLeftAxis,
    showRightAxis,
    leftAxisConfig,
    rightAxisConfig,
    locale,
    chartLines = 6,
    autarkyData,
    getSelectedTimeFrameIndex,
    showTooltip = false,
}: StackedBarChartProps): ReactElement => {
    const rightYAxisMaxValue = 100;

    const refToAdjustWidth = useRef<HTMLDivElement>(null);
    const { totalWidth: stackedBarTotalWidth, barGroupWidth: stackedBarGroupWidth } = useStackedBarWidth(
        isLoading,
        stackedBarData.length,
        refToAdjustWidth,
    );
    const maxEnergyYValue = calculateMaxEnergyYValue(stackedBarData);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const leftYLegendValues = getLabelsForYAxis(maxEnergyYValue, locale.toString(), chartLines);
    const rightYLegendValues = getLabelsForYAxis(rightYAxisMaxValue, locale.toString(), chartLines);

    useEffect(() => {
        if (selectedIndex != undefined && getSelectedTimeFrameIndex) getSelectedTimeFrameIndex(selectedIndex);
    }, [selectedIndex]);
    return (
        <>
            <Stack
                data-testid={"stacked-bar-chart-stack"}
                direction={"row"}
                width={"100%"}
                ref={refToAdjustWidth}
                mx={"32px"}
                justifyContent="space-around"
                zIndex={20}
            >
                <>
                    {stackedBarTotalWidth > 0 && (
                        <HoverItem
                            index={selectedIndex}
                            stackedBarGroupWidth={stackedBarGroupWidth}
                            stackedBarTotalWidth={stackedBarTotalWidth}
                            stackedBarData={stackedBarData}
                            setIndex={setSelectedIndex}
                            showTooltip={showTooltip}
                        />
                    )}
                    <StackedBarGroups
                        stackedBarData={stackedBarData}
                        width={stackedBarGroupWidth}
                        maxEnergyYValue={maxEnergyYValue}
                    />
                    {autarkyData && (
                        <AutarkyLine
                            autarkyData={autarkyData}
                            stackedBarGroupWidth={stackedBarGroupWidth}
                            stackedBarStackWidth={stackedBarTotalWidth}
                            stackedBarData={stackedBarData}
                        />
                    )}
                </>
            </Stack>
            {showLeftAxis && !leftAxisConfig && (
                <YAxisLegend variant={"left"} chartType={"bar"} yLegendValues={leftYLegendValues} unit={DataSeriesUnit.KWH} />
            )}
            {leftAxisConfig && (
                <YAxisLegend
                    variant={"left"}
                    chartType={"bar"}
                    yLegendValues={leftYLegendValues}
                    unit={leftAxisConfig.unit ? leftAxisConfig.unit : DataSeriesUnit.KWH}
                />
            )}
            {showRightAxis && !rightAxisConfig && (
                <YAxisLegend
                    variant={"right"}
                    chartType={"bar"}
                    yLegendValues={rightYLegendValues}
                    unit={DataSeriesUnit.PERCENT}
                />
            )}
            {rightAxisConfig && (
                <YAxisLegend
                    variant={"right"}
                    chartType={"bar"}
                    yLegendValues={rightYLegendValues}
                    unit={rightAxisConfig.unit ? rightAxisConfig.unit : DataSeriesUnit.PERCENT}
                />
            )}
            <ChartGrid amountLines={chartLines} />
        </>
    );
};

export function calculateMaxEnergyYValue(stackedBarDataList: StackedBarData[]): number {
    if (stackedBarDataList.length === 0) {
        return 0;
    }
    const maxEnergyYValue = Math.max(...stackedBarDataList.map((group) => calculateMaxYBarValueForGroup(group)));
    return maxEnergyYValue == 0 ? 1 : maxEnergyYValue;
}

function calculateMaxYBarValueForGroup(stackedBarData: StackedBarData): number {
    if (stackedBarData.stackedBarItems == null) return 0;
    return Math.max(
        ...stackedBarData.stackedBarItems.map((stack) => {
            if (!stack.isPercentageStack) {
                return (stack.lower?.value ?? 0) + (stack.middle?.value ?? 0) + (stack.upper?.value ?? 0);
            } else {
                return -Infinity;
            }
        }),
    );
}
